import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { CheckCircle2, Gift, Mail, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const GiftCardSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const giftCardId = params.get('gift_card_id');
  const orderId = params.get('order_id');
  const codeFromUrl = params.get('code');
  const isPending = params.get('pending') === '1';

  const [state, setState] = useState({ loading: true, code: codeFromUrl, amount: null, recipient: null });

  useEffect(() => {
    (async () => {
      // Already issued (points-only path) — just display
      if (codeFromUrl && !sessionId && !orderId) {
        setState({ loading: false, code: codeFromUrl, amount: null });
        return;
      }
      try {
        if (sessionId) {
          const r = await axios.post(`${API_URL}/api/gift-cards/confirm-stripe`, { session_id: sessionId });
          setState({ loading: false, code: r.data.code, amount: r.data.amount });
        } else if (giftCardId && orderId && !isPending) {
          const r = await axios.post(`${API_URL}/api/gift-cards/confirm-paypal`, { gift_card_id: giftCardId, order_id: orderId });
          setState({ loading: false, code: r.data.code, amount: r.data.amount });
        } else {
          setState({ loading: false });
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not confirm payment');
        setState({ loading: false, error: true });
      }
    })();
  }, [sessionId, giftCardId, orderId, codeFromUrl, isPending]);

  const copy = () => { navigator.clipboard.writeText(state.code); toast.success('Code copied!'); };

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />
      <div className="container mx-auto px-4 py-16 max-w-2xl" data-testid="gift-card-success-page">
        {state.loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-10 h-10 mx-auto text-[#FF3B6B] animate-spin" />
            <p className="text-[#6B6B7A] mt-3">Finalizing your gift…</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="inline-flex w-20 h-20 rounded-full bg-[#06D6A0]/10 items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#06D6A0]" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1E1E24] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {isPending ? 'Almost there!' : 'Gift card delivered! 🎉'}
            </h1>
            <p className="text-[#6B6B7A] max-w-md mx-auto mb-8">
              {isPending
                ? 'Your PayPal order is awaiting confirmation. You\'ll receive an email once it completes.'
                : 'A beautiful PDF voucher has been emailed to your recipient. They can redeem it on any party booking.'}
            </p>

            {state.code && (
              <div className="bg-white border-2 border-dashed border-[#FF3B6B] rounded-2xl p-6 mb-6 inline-block min-w-[280px]" data-testid="gc-success-code">
                <p className="text-xs text-[#6B6B7A] uppercase tracking-widest mb-2">Reference code</p>
                <p className="text-3xl font-bold text-[#FF3B6B] tracking-widest font-mono mb-3">{state.code}</p>
                <button onClick={copy} className="text-xs text-[#6B6B7A] hover:text-[#FF3B6B] inline-flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Copy code
                </button>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/my-gift-cards">
                <Button variant="outline" className="rounded-full" data-testid="gc-success-portal-btn">
                  <Gift className="w-4 h-4 mr-2" /> View my gift cards
                </Button>
              </Link>
              <Link to="/gift-cards">
                <Button variant="outline" className="rounded-full" data-testid="gc-success-buy-another-btn">
                  Buy another
                </Button>
              </Link>
              <Link to="/">
                <Button className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="gc-success-home-btn">
                  Back to home
                </Button>
              </Link>
            </div>

            <div className="mt-12 bg-white border border-[#E8E5DF] rounded-2xl p-5 text-left max-w-md mx-auto">
              <div className="flex items-center gap-2 mb-2"><Mail className="w-4 h-4 text-[#FF3B6B]" /><h3 className="font-semibold text-[#1E1E24] text-sm">What happens next?</h3></div>
              <ol className="text-xs text-[#6B6B7A] space-y-1.5 pl-4 list-decimal">
                <li>Recipient receives a celebratory email with the PDF voucher attached.</li>
                <li>They book a party at creativebirthdays.ca and enter the code at checkout.</li>
                <li>The voucher value is deducted from their total. Any unused balance stays on the card.</li>
              </ol>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default GiftCardSuccess;
